import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import { Input } from '@chakra-ui/input'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  HStack,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Spacer,
  Text as ChakraText,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsInfoCircleFill } from 'react-icons/bs'
import { CachePolicies, useFetch } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import useToast from '../../hooks/useToast'
import type { UserSessionResponse } from '../../interfaces'
import {
  DeveloperKey,
  RestrictedKey,
  RestrictedKeyErrorResponse,
} from '../../interfaces/DeveloperResponse'
import PasswordInput from '../PasswordInput'
import SetPasswordAlert from '../SetPasswordAlert'

interface Props {
  isCreate: boolean
  apiKey?: DeveloperKey
  apiKeyId?: string
  isOpen: boolean
  user: UserSessionResponse | null
  getData(): void
  onClose(): void
}

interface FormProps {
  email: string
  password: string
  mediaAPIRestriction: string
}

const CreateRestrictedApiKeyModal = ({
  isCreate,
  apiKey,
  apiKeyId,
  isOpen,
  onClose,
  user,
  getData,
}: Props): React.ReactElement => {
  const toast = useToast()
  const { t } = useTranslation()
  const [formError, setFormError] = useState<string>('')

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          enableReinitialize
          initialValues={{
            email: user?.userEmail ?? '',
            password: '',
            mediaAPIRestriction: isCreate
              ? 'none'
              : apiKey?.resourceAccess?.MEDIA_ACCESS ?? 'none',
          }}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
            if (user?.clientNumber) {
              let response
              if (isCreate) {
                response = (await request.post(
                  `/api/clients/${user.clientNumber}/settings/developer/keys/restricted/create`,
                  {
                    userEmail: values.email,
                    userPassword: values.password,
                    resourceAccess: {
                      MEDIA_ACCESS: values.mediaAPIRestriction,
                    },
                  },
                )) as RestrictedKey | RestrictedKeyErrorResponse
              } else {
                response = (await request.patch(
                  `/api/clients/${user.clientNumber}/settings/developer/keys/restricted/${apiKeyId}`,
                  {
                    userEmail: values.email,
                    userPassword: values.password,
                    resourceAccess: {
                      MEDIA_ACCESS: values.mediaAPIRestriction,
                    },
                  },
                )) as RestrictedKey | RestrictedKeyErrorResponse
              }
              if (request.error) {
                setFormError(
                  (response as RestrictedKeyErrorResponse).message ??
                    t(
                      'unknown_error_try_again',
                      'An unknown error occured, please try again.',
                    ),
                )
              } else {
                toast({
                  description: isCreate
                    ? t(
                        'successfully_created_restricted_api_keys',
                        'Successfully created new restricted API keys.',
                      )
                    : t(
                        'successfully_updated_restricted_api_keys_permissions',
                        'Successfully updated restricted API keys permissions.',
                      ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                void getData()
                onClose()
              }
            }
          }}
          validationSchema={Yup.object().shape({
            email: Yup.string().email().required('Required'),
            password: Yup.string().required('Required'),
            mediaAPIRestriction: Yup.mixed()
              .oneOf(['none', 'read', 'write'], 'Must be a valid restriction')
              .required('Required'),
          })}
        >
          {({ isSubmitting, submitForm, isValid, dirty }) => (
            <>
              <ModalHeader>
                {isCreate
                  ? t(
                      'create_new_restricted_api_key',
                      'Create new restricted API keys',
                    )
                  : t(
                      'update_restricted_api_key_permissions',
                      'Update restricted API keys permissions',
                    )}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {!user?.isPasswordSetByUser ? <SetPasswordAlert /> : null}
                {formError ? (
                  <Alert status="error" mb="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <VStack spacing={4} alignItems="flex-start">
                  <ChakraText>
                    {t(
                      'enter_password_to_continue',
                      'To continue, please enter your account password.',
                    )}
                  </ChakraText>
                  <Field name="email">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.email && form.touched.email,
                        )}
                        isReadOnly
                        isDisabled
                        isRequired
                      >
                        <FormLabel>{t('email', 'Email')}</FormLabel>
                        <Input {...field} placeholder="Email" />
                        <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="password">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.password && form.touched.password,
                        )}
                        isRequired
                      >
                        <FormLabel>{t('password', 'Password')}</FormLabel>
                        <PasswordInput
                          field={field}
                          isDisabled={!user?.isPasswordSetByUser}
                          isPasswordSetByUser={
                            user?.isPasswordSetByUser ?? false
                          }
                          showToggle={true}
                        />
                        {form.errors.password && form.touched.password ? (
                          <FormErrorMessage mt="0.25em">
                            {form.errors.password}
                          </FormErrorMessage>
                        ) : (
                          <Box height="1.55em" />
                        )}
                      </FormControl>
                    )}
                  </Field>
                  <Field name="mediaAPIRestriction">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.mediaAPIRestriction &&
                            form.touched.mediaAPIRestriction,
                        )}
                        isRequired
                      >
                        <Flex align="center">
                          <FormLabel>
                            {t(
                              'resource_access_restrictions',
                              'Resource access restrictions',
                            )}
                          </FormLabel>
                          <Popover trigger="hover" placement="top">
                            <PopoverTrigger>
                              <Box mb="0.5em">
                                <BsInfoCircleFill
                                  color={colors.appBlueyGrey}
                                  cursor="help"
                                />
                              </Box>
                            </PopoverTrigger>
                            <PopoverContent>
                              <PopoverArrow />
                              <PopoverBody>
                                Find the complete list of permissions on
                                resources{' '}
                                <Link
                                  lineHeight={1.2}
                                  href="https://docs.imagekit.io/api-reference/api-introduction/api-keys#resource-list"
                                  isExternal
                                >
                                  here
                                </Link>
                                .
                              </PopoverBody>
                            </PopoverContent>
                          </Popover>
                        </Flex>
                        <RadioGroup
                          {...field}
                          value={form.values.mediaAPIRestriction}
                        >
                          <HStack spacing="1em" alignItems="flex-start">
                            <Tooltip
                              label={t(
                                'media_access_resource_description',
                                'Permissions for operations on upload, media management, file metadata, and custom metadata management APIs.',
                              )}
                            >
                              <ChakraText>
                                {t('media_access', 'Media access')}
                              </ChakraText>
                            </Tooltip>
                            <Spacer />
                            <Radio value="none" onChange={form.handleChange}>
                              {t('none', 'None')}
                            </Radio>
                            <Radio value="read" onChange={form.handleChange}>
                              {t('read_only', 'Read only')}
                            </Radio>
                            <Radio value="write" onChange={form.handleChange}>
                              {t('read_and_write', 'Read and write')}
                            </Radio>
                          </HStack>
                        </RadioGroup>
                        {form.errors.mediaAPIRestriction &&
                        form.touched.mediaAPIRestriction ? (
                          <FormErrorMessage mt="0.25em">
                            {form.errors.mediaAPIRestriction}
                          </FormErrorMessage>
                        ) : (
                          <Box height="1.55em" />
                        )}
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isDisabled={!isValid || !dirty}
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default CreateRestrictedApiKeyModal
